import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type EmployeePayment = {
  _id: string;
  employee_id: string;
  amount: number;
  method?: string;
  reference?: string;
  note?: string;
  createdAt?: string;
  [key: string]: any;
};

export type EmployeePaymentsState = {
  byEmployeeId: Record<
    string,
    {
      items: EmployeePayment[];
      loading: boolean;
      error?: string | null;
      page: number;
      pageSize: number;
      total: number;
      creating: boolean;
    }
  >;
};

const initialState: EmployeePaymentsState = {
  byEmployeeId: {},
};

const ensureState = (
  state: EmployeePaymentsState,
  employeeId: string,
  defaults?: Partial<EmployeePaymentsState['byEmployeeId'][string]>,
) => {
  if (!state.byEmployeeId[employeeId]) {
    state.byEmployeeId[employeeId] = {
      items: [],
      loading: false,
      error: null,
      page: 1,
      pageSize: 100,
      total: 0,
      creating: false,
      ...(defaults || {}),
    };
  }
};

const employeePaymentsSlice = createSlice({
  name: 'backofficeEmployeePayments',
  initialState,
  reducers: {
    setPaymentsLoading: (
      state,
      action: PayloadAction<{ employeeId: string; loading: boolean }>,
    ) => {
      ensureState(state, action.payload.employeeId);
      state.byEmployeeId[action.payload.employeeId].loading =
        action.payload.loading;
    },
    setPaymentsError: (
      state,
      action: PayloadAction<{ employeeId: string; error: string | null }>,
    ) => {
      ensureState(state, action.payload.employeeId);
      state.byEmployeeId[action.payload.employeeId].error =
        action.payload.error;
    },
    setPayments: (
      state,
      action: PayloadAction<{
        employeeId: string;
        items: EmployeePayment[];
        total?: number;
        page?: number;
        pageSize?: number;
      }>,
    ) => {
      ensureState(state, action.payload.employeeId);
      const bucket = state.byEmployeeId[action.payload.employeeId];
      bucket.items = action.payload.items;
      if (typeof action.payload.total === 'number')
        bucket.total = action.payload.total;
      if (typeof action.payload.page === 'number')
        bucket.page = action.payload.page;
      if (typeof action.payload.pageSize === 'number')
        bucket.pageSize = action.payload.pageSize;
    },
    setPaymentsPagination: (
      state,
      action: PayloadAction<{
        employeeId: string;
        page?: number;
        pageSize?: number;
      }>,
    ) => {
      ensureState(state, action.payload.employeeId);
      const bucket = state.byEmployeeId[action.payload.employeeId];
      if (typeof action.payload.page === 'number')
        bucket.page = action.payload.page;
      if (typeof action.payload.pageSize === 'number')
        bucket.pageSize = action.payload.pageSize;
    },
    setCreatingPayment: (
      state,
      action: PayloadAction<{ employeeId: string; creating: boolean }>,
    ) => {
      ensureState(state, action.payload.employeeId);
      state.byEmployeeId[action.payload.employeeId].creating =
        action.payload.creating;
    },
    addPayment: (
      state,
      action: PayloadAction<{ employeeId: string; payment: EmployeePayment }>,
    ) => {
      ensureState(state, action.payload.employeeId);
      const bucket = state.byEmployeeId[action.payload.employeeId];
      bucket.items = [action.payload.payment, ...bucket.items];
      bucket.total = bucket.total + 1;
    },
  },
});

export const {
  setPaymentsLoading,
  setPaymentsError,
  setPayments,
  setPaymentsPagination,
  setCreatingPayment,
  addPayment,
} = employeePaymentsSlice.actions;

export default employeePaymentsSlice.reducer;
